import { externalUrl } from "@/lib/externalUrl";

/**
 * The player address for a Vimeo or YouTube link, or null for anything else.
 *
 * A profile video used to be a bare `<video src>`, which plays a video *file*
 * and nothing else. Nobody has one of those: members paste the address of the
 * page the video is on, which a `<video>` element cannot play whatever its
 * settings, so every Vimeo link on a profile was a dead black box. (Asked
 * about on 18 September, before a members' walkthrough.) Those two hosts get
 * their own player in an iframe; any other address is still tried as a file.
 *
 * Only the video id is carried over, never the pasted URL itself, so what
 * lands in the iframe is always one of the two players below.
 */
export function videoEmbedUrl(value: string | null | undefined): string | null {
  const href = externalUrl(value);
  if (!href) return null;
  let url: URL;
  try {
    url = new URL(href);
  } catch {
    return null;
  }
  const host = url.hostname.toLowerCase().replace(/^(www|m)\./, "");
  const path = url.pathname.split("/").filter(Boolean);

  if (host === "youtu.be" || host === "youtube.com" || host === "youtube-nocookie.com") {
    const id =
      host === "youtu.be"
        ? path[0]
        : url.searchParams.get("v") ??
          (["embed", "shorts", "live", "v"].includes(path[0]) ? path[1] : undefined);
    if (!id || !/^[\w-]{6,20}$/.test(id)) return null;
    const start = youTubeStart(url.searchParams.get("t") ?? url.searchParams.get("start"));
    return `https://www.youtube-nocookie.com/embed/${id}${start ? `?start=${start}` : ""}`;
  }

  if (host === "vimeo.com" || host === "player.vimeo.com") {
    // vimeo.com/123, /channels/x/123, /groups/x/videos/123, /showcase/x/video/123
    // and player.vimeo.com/video/123 all carry the id as the first number.
    const at = path.findIndex((part) => /^\d+$/.test(part));
    if (at < 0) return null;
    const id = path[at];
    // An unlisted video is only playable with its hash: vimeo.com/123/abc, or
    // ?h=abc on a player link. Without it Vimeo refuses to play.
    const next = path[at + 1];
    const hash =
      url.searchParams.get("h") ?? (next && /^[0-9a-f]{6,}$/i.test(next) ? next : null);
    return `https://player.vimeo.com/video/${id}${hash ? `?h=${hash}` : ""}`;
  }

  return null;
}

/** `t=90`, `t=90s` or `t=1m30s` as whole seconds. */
function youTubeStart(t: string | null): number | null {
  if (!t) return null;
  const m = /^(?:(\d+)h)?(?:(\d+)m)?(?:(\d+)s?)?$/.exec(t);
  if (!m) return null;
  const seconds = Number(m[1] ?? 0) * 3600 + Number(m[2] ?? 0) * 60 + Number(m[3] ?? 0);
  return seconds || null;
}
